/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import Mixin from '../../../util/mixin';
import { math } from 'polished';
import { MediaBreakpointUp, breakpointInfix } from './_breakpoints.mixin';
import { MakeCol, MakeColAuto, RowCols } from './_grid.css';

// Framework grid generation
//
// Used only by Bootstrap to generate the correct number of grid classes given
// any value of `$grid-columns`.
export class MakeGridColumns extends Mixin {
  include($) {
    const [$columns = $.gridColumns, $gutter = $.gridGutterWidth, $breakpoints = $.gridBreakpoints] = this.args ?? [];

    // Extract numeric value from gutter (e.g., "30px" -> 30)
    const gutterValue = parseFloat($gutter);
    const gutterUnit = $gutter.replace(/[\d.]/g, ''); // Extract unit (e.g., "px")

    // Common properties for all breakpoints
    const gridColumnStyle = css`
      position: relative;
      width: 100%;
      padding-right: ${math(`${gutterValue} * 0.5`)}${gutterUnit};
      padding-left: ${math(`${gutterValue} * 0.5`)}${gutterUnit};
    `;

    const rules = [];
    Object.keys($breakpoints).forEach(($breakpoint) => {
      const $infix = breakpointInfix($, $breakpoint, $breakpoints);

      if ($columns > 0) {
        // Allow columns to stretch full width below their breakpoints
        for (let $i = 1; $i <= $columns; $i += 1) {
          rules.push(css`
            .col${$infix}-${$i} {
              ${gridColumnStyle};
            }
          `);
        }
      }

      rules.push(css`
        .col${$infix},
        .col${$infix}-auto {
          ${gridColumnStyle};
        }
      `)

      const rowColRules = $.gridRowColumns > 0 ? css(
        Array.from({ length: $.gridRowColumns }, (_, $i) => css`
            .row-cols${$infix}-${$i} {
              ${new RowCols($i).include($)}
            }
        `)
      ) : css``;

      const colRules = $columns > 0 ? css(
          Array.from({ length: $columns }, (_, $i) => css`
            .col${$infix}-${$i} {
              ${new MakeCol($i, $columns).include($)}
            }
          `)
      ) : css``;

      const colOffsetRules = $columns > 0 ? css(
        // `$columns - 1` because offsetting by the width of an entire row isn't possible
        Array.from({ length: $columns - 1 }, (_, $i) =>
          ($infix == "" && $i == 0) ? ( // Avoid emitting useless .offset-0
            null
          ) : (
            css`
              .offset${$infix}-${$i} {
                ${new MakeCol($i, $columns).include($)}
              }
            `
          )
        ).filter(x => !!x)
      ) : css``;

      rules.push(new MediaBreakpointUp($breakpoint, $breakpoints).include($, css`
        // Provide basic \`.col-{bp}\` classes for equal-width flexbox columns
        .col${$infix} {
          flex-basis: 0;
          flex-grow: 1;
          max-width: 100%;
        }

        ${rowColRules}

        .col${$infix}-auto {
          ${new MakeColAuto().include($)}
        }

        ${colRules}

        .order${$infix}-first { order: -1; }

        .order${$infix}-last { order: $columns + 1; }

        ${css(Array.from({ length: $columns }, (_, $i) => css`
          .order${$infix}-${$i} { order: ${$i}; }
        `))}

        ${colOffsetRules}
      `));
    });

    return css(rules);
  }
}